import type { ReactNode } from "react";

/**
 * iPhone 15 Pro-ish frame. 393×852 dp scaled to fit a 320×690 canvas
 * area. Dynamic Island as a black pill at the top.
 */
export function PhoneFrame({
  children,
  width = 320,
}: {
  children?: ReactNode;
  width?: number;
}) {
  const height = Math.round(width * (852 / 393));
  return (
    <div
      style={{
        position: "relative",
        width,
        height,
        borderRadius: 44,
        background: "#0b0d12",
        border: "1px solid #2a2f3a",
        padding: 6,
        boxShadow:
          "0 30px 80px -20px rgba(0,0,0,0.5), inset 0 0 0 2px #15181f",
        flexShrink: 0,
      }}
    >
      {/* Screen */}
      <div
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 38,
          background: "#0F1115",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Dynamic Island */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 10,
            left: "50%",
            transform: "translateX(-50%)",
            width: 96,
            height: 28,
            borderRadius: 14,
            background: "#000",
            zIndex: 2,
          }}
        />
        {/* Status bar spacer */}
        <div style={{ height: 48, flexShrink: 0 }} />
        <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>{children}</div>
      </div>
    </div>
  );
}

/**
 * Browser chrome frame for `web` project previews. The top bar shows
 * traffic-light dots + URL on the left; Reimagine + Preview affordances
 * on the right (matches Replit's preview header in the user reference
 * screenshot).
 */
export function BrowserFrame({
  children,
  url = "https://preview.vor.codes",
}: {
  children?: ReactNode;
  url?: string;
}) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        maxWidth: 1080,
        maxHeight: 720,
        borderRadius: 14,
        background: "var(--bg-panel)",
        border: "1px solid var(--border-strong)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 20px 60px -20px rgba(0,0,0,0.4)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          borderBottom: "1px solid var(--border-default)",
        }}
      >
        <span aria-hidden style={{ display: "inline-flex", gap: 6 }}>
          <Dot color="#ef4444" />
          <Dot color="#f59e0b" />
          <Dot color="#10b981" />
        </span>
        <div
          style={{
            flex: 1,
            padding: "3px 10px",
            background: "var(--bg-canvas)",
            border: "1px solid var(--border-default)",
            borderRadius: 999,
            fontSize: 11,
            color: "var(--text-tertiary)",
            textAlign: "center",
          }}
        >
          {url}
        </div>
        <button
          type="button"
          data-testid="preview-reimagine"
          onClick={() =>
            window.dispatchEvent(new CustomEvent("vp:preview:reimagine"))
          }
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            background: "transparent",
            border: "1px solid var(--border-default)",
            borderRadius: 6,
            color: "var(--text-secondary)",
            fontSize: 11,
            cursor: "pointer",
          }}
          title="Reimagine — ask the agent to rebuild this view"
        >
          ✦ Reimagine...
        </button>
        <span
          aria-hidden
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            background: "var(--bg-canvas)",
            border: "1px solid var(--border-default)",
            borderRadius: 6,
            color: "var(--text-tertiary)",
            fontSize: 11,
          }}
        >
          ▷ Preview
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0, background: "#0F1115" }}>{children}</div>
    </div>
  );
}

function Dot({ color }: { color: string }) {
  return <span style={{ width: 10, height: 10, borderRadius: 5, background: color, display: "inline-block" }} />;
}
